import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AgreementService } from '../../../services/agreement.service';
import { Agreement } from '../../../models/Agreement';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { CustomersService } from '../../../services/customers.service';
import { AuthService } from '../../../services/auth.service';

interface AgreementFilters {
  status: string | null;
  searchTerm: string;
  page: number;
  pageSize: number;
}

@Component({
  selector: 'app-agreements',
  templateUrl: './agreements.component.html',
  styleUrls: ['./agreements.component.css']
})
export class AgreementsComponent implements OnInit, OnDestroy {
  // Configuração da tabela
  tableTitle = 'Gestão de Acordos';
  showSearch = true;
  searchPlaceholder = 'Buscar acordos...';
  clickable = true;

  // Colunas da tabela
  columns = [
    { titulo: 'Número', label: 'agreementNumber' },
    { titulo: 'Cliente', label: 'customerName' },
    { titulo: 'Locação', label: 'vehicleRentalNumber' },
    { titulo: 'Valor Original', label: 'totalOriginalAmount', pipe: 'currency' },
    { titulo: 'Valor Acordado', label: 'totalAgreedAmount', pipe: 'currency' },
    { titulo: 'Desconto', label: 'discount', pipe: 'currency' },
    { titulo: 'Vencimento', label: 'dueDate', pipe: 'date' },
    { titulo: 'Parcelas', label: 'installments' },
    { titulo: 'Status', label: 'status', badge: true }
  ];

  // Ações da tabela
  actions = [
    {
      label: 'Visualizar',
      icon: 'fas fa-eye',
      action: 'view',
      class: 'btn-view'
    },
    {
      label: 'Editar',
      icon: 'fas fa-pen-to-square',
      action: 'edit',
      class: 'btn-edit'
    },
    {
      label: 'Cancelar',
      icon: 'fas fa-ban',
      action: 'cancel',
      class: 'btn-cancel'
    }
  ];

  // Dados
  agreements: Agreement[] = [];
  loading = false;
  error: string | null = null;

  // Paginação
  currentPage = 1;
  pageSize = 50;
  totalItems = 0;
  totalPages = 0;
  hasNextPage = false;
  hasPreviousPage = false;

  // Filtros
  filters: AgreementFilters = {
    status: null,
    searchTerm: '',
    page: 1,
    pageSize: 50
  };

  // Opções para filtros
  statusOptions = [
    { value: 'Pendente', label: 'Pendente' },
    { value: 'Ativo', label: 'Ativo' },
    { value: 'Quitado', label: 'Quitado' },
    { value: 'Cancelado', label: 'Cancelado' }
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private agreementService: AgreementService,
    private router: Router,
    private customersService: CustomersService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loadAgreements();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadAgreements(): void {
    this.loading = true;
    this.error = null;

    const queryParams: any = {
      page: this.filters.page,
      pageSize: this.filters.pageSize
    };

    if (this.filters.status) {
      queryParams.status = this.filters.status;
    }

    if (this.filters.searchTerm) {
      queryParams.search = this.filters.searchTerm;
    }

    this.agreementService.getAll(queryParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: any) => {
          if (Array.isArray(data)) {
            this.agreements = data;
            this.totalItems = data.length;
          } else {
            this.agreements = data.items || [];
            this.totalItems = data.total || 0;
            this.totalPages = data.totalPages || 1;
            this.hasNextPage = data.hasNextPage || false;
            this.hasPreviousPage = data.hasPreviousPage || false;
          }
          this.enrichCustomerNames(this.agreements);
          this.loading = false;
        },
        error: (err: any) => {
          this.error = 'Erro ao carregar acordos. Tente novamente.';
          this.loading = false;
          console.error('Erro ao carregar acordos:', err);
        }
      });
  }

  onActionClick(event: { action: string; item: Agreement }): void {
    switch (event.action) {
      case 'view':
        this.view(event.item.id);
        break;
      case 'edit':
        this.edit(event.item.id);
        break;
      case 'cancel':
        this.cancelAgreement(event.item.id);
        break;
      default:
        break;
    }
  }

  onSearchChange(searchTerm: string): void {
    this.filters.searchTerm = searchTerm;
    this.filters.page = 1;
    this.currentPage = 1;
    this.loadAgreements();
  }

  onStatusFilterChange(status: string | null): void {
    this.filters.status = status || null;
    this.filters.page = 1;
    this.currentPage = 1;
    this.loadAgreements();
  }

  onPageChange(page: number): void {
    this.filters.page = page;
    this.currentPage = page;
    this.loadAgreements();
  }

  onPageSizeChange(pageSize: number): void {
    this.filters.pageSize = pageSize;
    this.filters.page = 1;
    this.currentPage = 1;
    this.loadAgreements();
  }

  clearFilters(): void {
    this.filters = {
      status: null,
      searchTerm: '',
      page: 1,
      pageSize: 50
    };
    this.currentPage = 1;
    this.loadAgreements();
  }

  newAgreement(): void {
    this.router.navigate(['/financial/agreements/new']);
  }

  view(id: number): void {
    this.router.navigate(['/financial/agreements', id]);
  }

  edit(id: number): void {
    this.router.navigate(['/financial/agreements', id, 'edit']);
  }

  private enrichCustomerNames(agreements: Agreement[]): void {
    const missingIds = [
      ...new Set(
        agreements
          .filter(a => a.customerId && !(a.customerName || '').trim())
          .map(a => a.customerId)
      )
    ];
    if (missingIds.length === 0) return;

    forkJoin(
      missingIds.map(id =>
        this.customersService.getById(id).pipe(
          map(c => ({ id, name: c.name })),
          catchError(() => of({ id, name: '' }))
        )
      )
    )
      .pipe(takeUntil(this.destroy$))
      .subscribe(results => {
        const nameById = new Map(results.map(r => [r.id, r.name]));
        this.agreements = this.agreements.map(a => {
          if ((a.customerName || '').trim()) return a;
          const name = nameById.get(a.customerId);
          return name ? { ...a, customerName: name } : a;
        });
      });
  }

  cancelAgreement(id: number): void {
    if (confirm('Tem certeza que deseja cancelar este acordo?')) {
      this.loading = true;
      const cancelledBy =
        this.authService.getUserNameFromToken() ||
        this.authService.currentUserValue?.username ||
        'system';
      this.agreementService.cancel(id, cancelledBy)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.loadAgreements();
          },
          error: (err: any) => {
            this.error = 'Erro ao cancelar acordo.';
            this.loading = false;
            console.error('Erro ao cancelar acordo:', err);
          }
        });
    }
  }

  retryLoad(): void {
    this.error = null;
    this.loadAgreements();
  }
}
